import { useState, useEffect } from 'react';
import { X, ShareNetwork, PlusCircle, DownloadSimple } from '@phosphor-icons/react';

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

export default function InstallPrompt({ variant = 'default' }) {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  const isScoring = variant === 'scoring';

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem('foretour:installDismissed') === 'true') return;

    // Track visits (dedupe with sessionStorage)
    if (!sessionStorage.getItem('foretour:visitCounted')) {
      const count = parseInt(localStorage.getItem('foretour:visitCount') || '0', 10) + 1;
      localStorage.setItem('foretour:visitCount', String(count));
      sessionStorage.setItem('foretour:visitCounted', '1');
    }

    const visitCount = parseInt(localStorage.getItem('foretour:visitCount') || '0', 10);
    if (visitCount < 2) return;

    // Scoring variant: show on all platforms (informational card, no native install API needed)
    if (isScoring) {
      setShow(true);
      return;
    }

    if (isIOS()) {
      setIsIOSDevice(true);
      setShow(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isScoring]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem('foretour:installDismissed', 'true');
    setShow(false);
  };

  if (!show) return null;

  // Scoring variant: sticky bottom card with amber accent
  if (isScoring) {
    return (
      <div className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+72px)] left-0 right-0 z-40 px-4 animate-fade-in-up" data-testid="install-prompt-scoring">
        <div className="p-4 bg-[#FDF8E8] border-2 border-[#C9A227] rounded-xl shadow-lg max-w-lg mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#C9A227] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">FT</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#1D242B]">Install for reliable scoring</p>
              <p className="text-xs text-[#455561] mt-1">
                Add to your Home Screen to prevent session loss during rounds.
              </p>
            </div>
            <button onClick={handleDismiss} className="text-[#B8C7C0] hover:text-[#455561] p-1 -mt-1 -mr-1 transition-colors" data-testid="install-dismiss">
              <X className="w-4 h-4" weight="bold" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-4 p-4 bg-white border border-[#E1E6E4] rounded-xl shadow-sm" data-testid="install-prompt">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1B4332] flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">FT</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#1D242B]">Install Fore Tour</p>
          {isIOSDevice ? (
            <p className="text-xs text-[#455561] mt-1">
              Tap <ShareNetwork className="inline w-4 h-4 text-[#007AFF] -mt-0.5" weight="bold" /> Share then <span className="font-semibold">Add to Home Screen</span> <PlusCircle className="inline w-4 h-4 text-[#455561] -mt-0.5" weight="bold" />
            </p>
          ) : (
            <p className="text-xs text-[#455561] mt-1">Add to your home screen for the full experience</p>
          )}
        </div>
        <button onClick={handleDismiss} className="text-[#B8C7C0] p-1 -mt-1 -mr-1" data-testid="install-dismiss">
          <X className="w-4 h-4" weight="bold" />
        </button>
      </div>
      {!isIOSDevice && deferredPrompt && (
        <button
          onClick={handleInstall}
          className="mt-3 w-full h-10 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
          data-testid="install-btn"
        >
          <DownloadSimple className="w-4 h-4" weight="bold" /> Install App
        </button>
      )}
    </div>
  );
}
